import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import Layout from './components/layout/Layout';
import Login from './components/auth/Login';
import Logout from './components/auth/Logout';
import TwoFactorSetup from './components/auth/TwoFactorSetup';
import Callback from './components/auth/Callback';
import ProtectedRoute from './components/auth/ProtectedRoute';
import CompanySelectionWrapper from './components/auth/CompanySelectionWrapper';
import NotFoundPage from './components/errors/NotFoundPage';
import { useAuth } from './context/AuthContext';
import { AdminDataProvider } from './context/AdminDataContext';

// Lazy load components to enable code splitting
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const CompanyList = lazy(() => import('./components/company/CompanyList'));
const EmailConfig = lazy(() => import('./components/email/EmailConfig'));
const UserSettings = lazy(() => import('./components/auth/UserSettings'));
const UserList = lazy(() => import('./components/users/UserList'));
const SmsLogs = lazy(() => import('./components/logs/SmsLogs'));
const AdminGroupManagement = lazy(() => import('./components/admin/AdminGroupManagement'));
const AdminUserCompanyManagement = lazy(() => import('./components/admin/AdminUserCompanyManagement'));
const LockedAccountsManager = lazy(() => import('./components/admin/LockedAccountsManager'));
const SMTPPoolManagement = lazy(() => import('./components/admin/SMTPPoolManagement'));
const CompanyTokenManagement = lazy(() => import('./components/admin/CompanyTokenManagement'));
const AdminSessionManagement = lazy(() => import('./components/admin/AdminSessionManagement'));
const QueueManagement = lazy(() => import('./components/admin/QueueManagement'));

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="200px"
  >
    <CircularProgress />
  </Box>
);

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} 
      />
      <Route 
        path="/setup-2fa" 
        element={<TwoFactorSetup />} 
      />
      <Route path="/auth/logout" element={<Logout />} />
      <Route path="/auth/callback" element={<Callback />} />
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <CompanySelectionWrapper>
              <Layout />
            </CompanySelectionWrapper>
          </ProtectedRoute>
        }
      >
        <Route index element={
          <Suspense fallback={<LoadingSpinner />}>
            <Dashboard />
          </Suspense>
        } />
        <Route 
          path="companies" 
          element={
            <ProtectedRoute roles={['admin', 'superadmin']}>
              <Suspense fallback={<LoadingSpinner />}>
                <CompanyList />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route path="email-config" element={
          <Suspense fallback={<LoadingSpinner />}>
            <EmailConfig />
          </Suspense>
        } />
        <Route path="sms-logs" element={
          <Suspense fallback={<LoadingSpinner />}>
            <SmsLogs />
          </Suspense>
        } />
        <Route path="settings" element={
          <Suspense fallback={<LoadingSpinner />}>
            <UserSettings />
          </Suspense>
        } />
        <Route 
          path="users" 
          element={
            <ProtectedRoute roles={['admin', 'superadmin']}>
              <Suspense fallback={<LoadingSpinner />}>
                <UserList />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        {/* Admin Routes with Shared Data Context */}
        <Route 
          path="admin/*" 
          element={
            <ProtectedRoute roles={['admin', 'superadmin']}>
              <AdminDataProvider>
                <Routes>
                  <Route 
                    path="groups" 
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <AdminGroupManagement />
                      </Suspense>
                    }
                  />
                  <Route 
                    path="user-companies" 
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <AdminUserCompanyManagement />
                      </Suspense>
                    }
                  />
                  <Route 
                    path="locked-accounts" 
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <LockedAccountsManager />
                      </Suspense>
                    }
                  />
                  <Route 
                    path="smtp-pools" 
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <SMTPPoolManagement />
                      </Suspense>
                    }
                  />
                  <Route 
                    path="tokens" 
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <CompanyTokenManagement />
                      </Suspense>
                    }
                  />
                  <Route 
                    path="sessions" 
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <AdminSessionManagement />
                      </Suspense>
                    }
                  />
                  <Route 
                    path="queue" 
                    element={
                      <Suspense fallback={<LoadingSpinner />}>
                        <QueueManagement />
                      </Suspense>
                    }
                  />
                </Routes>
              </AdminDataProvider>
            </ProtectedRoute>
          } 
        />
        <Route path="dashboard" element={<Navigate to="/" replace />} />
      </Route>
      {/* 404 Route - MUST be outside protected routes to work properly */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
